import type { FC } from 'react';

import type { UseFieldArrayReturn, UseFormReturn } from 'react-hook-form';

import { isStringValidE164Format } from '@polyai/common/lib/stringHelper';
import { TrashCanIcon } from '@polyai/ui/components/atoms/Icons';
import Input from '@polyai/ui/components/atoms/Input';
import { Stack } from '@polyai/ui/components/atoms/Stack';

import { SMS_TEMPLATES } from 'screens/SmsTemplates/SmsTemplates.uiCopy';
import * as Styled from './ConnectionModalContent.styled';
export interface ConnectionFormData {
  account_sid: string;
  auth_token: string;
  numbers: {
    value: string;
  }[];
}

interface ConnectionModalContentProps {
  connection: ConnectionFormData;
  formMethods: UseFormReturn<ConnectionFormData>;
  fieldMethods: UseFieldArrayReturn<ConnectionFormData>;
}

const isValidMessagingServiceId = (value: string): boolean => {
  const mgRegex = /^MG[a-z0-9]+$/;
  return mgRegex.test(value);
};

const validatePhoneNumberOrMessagingServiceId = (
  value: string,
  formData: ConnectionFormData,
) => {
  const filtered = formData.numbers.filter(n => n.value);

  if (!filtered.length) {
    return false;
  }

  if (!value) {
    return true;
  }

  const isValidPhoneNumber = isStringValidE164Format(value);

  const isValidMessagingService = isValidMessagingServiceId(value);

  if (isValidPhoneNumber || isValidMessagingService) {
    return true;
  }

  if (value.startsWith('MG')) {
    return 'Invalid Messaging Service ID format. Must be MG followed by exactly 32 hexadecimal characters.';
  }

  if (value.includes('+')) {
    return 'Invalid phone number format. Please use E.164 format (e.g., +1234567890).';
  }

  return 'Please enter a valid phone number (E.164 format) or Messaging Service ID (MG format).';
};

const ConnectionModalContent: FC<ConnectionModalContentProps> = ({
  connection,
  formMethods,
  fieldMethods,
}) => {
  const { register, formState } = formMethods;
  const { fields, append, remove } = fieldMethods;
  const { errors } = formState;

  if (connection.numbers?.at(-1)?.value !== '') {
    append({ value: '' }, { shouldFocus: false });
  }

  const handleDeleteRow = (idx: number) => {
    remove(idx);
  };

  return (
    <>
      <Styled.SubHeading>
        {SMS_TEMPLATES.modal.twilio.description(text => (
          <Styled.TwilioLink href="https://console.twilio.com" target="_blank">
            {text}
          </Styled.TwilioLink>
        ))}
      </Styled.SubHeading>
      <Input
        {...register('account_sid', {
          required: SMS_TEMPLATES.modal.twilio.form.accountSID.requiredError,
        })}
        help={SMS_TEMPLATES.modal.twilio.form.accountSID.helpText.default}
        label={SMS_TEMPLATES.modal.twilio.form.accountSID.label}
        placeholder={SMS_TEMPLATES.modal.twilio.form.accountSID.placeholder}
        fullWidth
      />
      <Input
        {...register('auth_token', {
          required: SMS_TEMPLATES.modal.twilio.form.authToken.requiredError,
        })}
        help={SMS_TEMPLATES.modal.twilio.form.authToken.helpText}
        label={SMS_TEMPLATES.modal.twilio.form.authToken.label}
        placeholder={SMS_TEMPLATES.modal.twilio.form.authToken.placeholder}
        fullWidth
      />
      <Stack direction="column" spacing="xs1">
        {fields.map((num, idx) => {
          const errorForRow = errors.numbers
            ? errors.numbers[idx]?.value?.message
            : '';
          const isFirst = idx === 0;
          return (
            <Styled.SmsNumberInputsSection
              key={num.id}
              direction="row"
              spacing="xs2"
            >
              <Styled.SmsNumberInputWrapper>
                <Input
                  {...register(`numbers.${idx}.value`, {
                    validate: { validatePhoneNumberOrMessagingServiceId },
                  })}
                  data-test-id="twilio-number-input"
                  errors={errorForRow ? [errorForRow] : undefined}
                  help={
                    isFirst &&
                    SMS_TEMPLATES.modal.twilio.form.phoneNumber.helpText
                  }
                  label={
                    isFirst && SMS_TEMPLATES.modal.twilio.form.phoneNumber.label
                  }
                  placeholder={
                    isFirst
                      ? SMS_TEMPLATES.modal.twilio.form.phoneNumber.placeholder
                      : undefined
                  }
                  fullWidth
                />
              </Styled.SmsNumberInputWrapper>

              <Styled.DeleteButton
                $hasErrors={!!errorForRow}
                $isFirstRow={!idx}
                disabled={idx === fields.length - 1}
                onClick={() => handleDeleteRow(idx)}
              >
                <TrashCanIcon />
              </Styled.DeleteButton>
            </Styled.SmsNumberInputsSection>
          );
        })}
      </Stack>
    </>
  );
};

export default ConnectionModalContent;
